"use client";

import { cn } from "@ui/lib/utils";
import * as m from "motion/react-m";
import { useEffect, useState } from "react";

export interface Orb2Props {
  className?: string;
  getInputVolume?: () => number;
  getOutputVolume?: () => number;
  size?: number; // Size in pixels
}

export default function Orb2({ getInputVolume, getOutputVolume, className, size = 70 }: Orb2Props) {
  const [smoothedVolume, setSmoothedVolume] = useState(0);

  useEffect(() => {
    if (!getInputVolume && !getOutputVolume) return;

    const interval = setInterval(() => {
      const inputVolume = getInputVolume?.() ?? 0;
      const outputVolume = getOutputVolume?.() ?? 0;
      const volume = Math.max(inputVolume, outputVolume);
      const amplifiedVolume = Math.min(volume * 3, 1);

      setSmoothedVolume(amplifiedVolume);
    }, 100);

    return () => clearInterval(interval);
  }, [getInputVolume, getOutputVolume]);

  const volumeScale = 1 + smoothedVolume * 0.4;
  const volumeOpacity = 0.6 + smoothedVolume * 0.4;
  const volumeRotationSpeed = 1 + smoothedVolume * 2; // Faster rotation when speaking
  const volumeScaleVariation = 0.02 + smoothedVolume * 0.08; // More dramatic scaling when speaking

  return (
    <div
      className={cn("relative flex items-center justify-center overflow-hidden rounded-full", className)}
      style={{ width: size, height: size }}
    >
      {/* Layer 1: Conic gradient background */}
      <div
        className='absolute inset-0 rounded-full'
        style={{
          background: `conic-gradient(from 180deg at 50% 50%, #3D75D8 11.59305989742279deg, #2A68D2 26.316995322704315deg, #75BEE5 32.392407953739166deg, #52D0E9 38.91463100910187deg, #2159BA 75.35075604915619deg, #3A74DA 85.0388252735138deg, #6CD7EC 94.52927470207214deg, #5AB9F1 122.11026906967163deg, #33A1E5 129.96727466583252deg, #1F5FCF 136.71944618225098deg, #2C54CA 144.31334853172302deg, #3A9ECF 175.98480820655823deg, #3167C5 183.86929035186768deg, #ADE8F3 201.63469791412354deg, #D8F1F5 224.86509561538696deg, #A5DBE6 239.72861051559448deg, #2B9DD6 268.16500425338745deg, #AEE0E9 286.21137857437134deg, #20BAD0 329.6537446975708deg, #1E53B0 343.5508704185486deg, #2F40D2 359.2553758621216deg)`,
        }}
      />

      {/* Layer 2: Animated blob - contained within the orb */}
      <m.div
        animate={{
          scale: volumeScale,
          opacity: volumeOpacity,
        }}
        className='absolute inset-0 flex items-center justify-center overflow-hidden rounded-full'
        transition={{
          scale: { duration: 0.2, ease: "easeOut" },
          opacity: { duration: 0.3, ease: "easeOut" },
        }}
      >
        <m.div
          animate={{
            rotate: 360,
            scale: [1, 1 + volumeScaleVariation, 1 - volumeScaleVariation, 1],
            scaleX: [1, 1 + volumeScaleVariation * 0.5, 1 - volumeScaleVariation * 0.3, 1], // Horizontal morphing
            scaleY: [1, 1 - volumeScaleVariation * 0.3, 1 + volumeScaleVariation * 0.5, 1], // Vertical morphing
          }}
          style={{
            width: size * 0.85, // Reduced size to ensure it stays within bounds
            height: size * 0.76, // Adjusted proportionally
            mixBlendMode: "color-burn",
          }}
          transition={{
            rotate: {
              duration: 20 / volumeRotationSpeed,
              ease: "linear",
              repeat: Infinity,
            },
            scale: {
              duration: 3 - smoothedVolume * 1.5, // Faster pulsing when speaking
              ease: "easeInOut",
              repeat: Infinity,
            },
            scaleX: {
              duration: 2.5 - smoothedVolume * 1,
              ease: "easeInOut",
              repeat: Infinity,
            },
            scaleY: {
              duration: 3.2 - smoothedVolume * 1.2,
              ease: "easeInOut",
              repeat: Infinity,
            },
          }}
        >
          <svg fill='none' height='100%' style={{ width: "100%", height: "100%" }} viewBox='0 0 69 62' width='100%'>
            <g style={{ mixBlendMode: "color-burn" }}>
              <path
                d='M68.7734 33.1565C68.7734 50.8143 54.8419 50.2083 37.1595 57.6685C9.29647 69.4239 -3.02753 48.6097 1.79535 33.1565C6.24263 18.9068 13.2565 20.2731 18.9414 14.5061C24.7763 8.58692 27.0948 0.118652 36.0884 0.118652C53.8442 0.118652 68.7734 15.4988 68.7734 33.1565Z'
                fill='#1C366A'
                fillOpacity='0.35'
              />
            </g>
          </svg>
        </m.div>
      </m.div>

      {/* Layer 3: Overlay with inner shadow */}
      <div
        className='absolute inset-0 rounded-full'
        style={{
          width: size,
          height: size,
        }}
      >
        <svg
          fill='none'
          height='100%'
          style={{ width: "100%", height: "100%" }}
          viewBox='0 0 407 407'
          width='100%'
          xmlns='http://www.w3.org/2000/svg'
        >
          <g filter='url(#a)'>
            <circle cx='203.5' cy='203.5' fill='#16E1FF' fillOpacity='.01' r='203.5' />
          </g>
          <defs>
            <filter
              colorInterpolationFilters='sRGB'
              filterUnits='userSpaceOnUse'
              height='453.514'
              id='a'
              width='407'
              x='0'
              y='-17.443'
            >
              <feFlood floodOpacity='0' result='BackgroundImageFix' />
              <feBlend in='SourceGraphic' in2='BackgroundImageFix' result='shape' />
              <feColorMatrix in='SourceAlpha' result='hardAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' />
              <feOffset dy='29.071' />
              <feGaussianBlur stdDeviation='58.143' />
              <feComposite in2='hardAlpha' k2='-1' k3='1' operator='arithmetic' />
              <feColorMatrix values='0 0 0 0 0.552941 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0' />
              <feBlend in2='shape' result='effect1_innerShadow_1248_7583' />
              <feColorMatrix in='SourceAlpha' result='hardAlpha' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0' />
              <feOffset dy='-17.443' />
              <feGaussianBlur stdDeviation='23.257' />
              <feComposite in2='hardAlpha' k2='-1' k3='1' operator='arithmetic' />
              <feColorMatrix values='0 0 0 0 0.153563 0 0 0 0 0.272554 0 0 0 0 0.748518 0 0 0 0.6 0' />
              <feBlend in2='effect1_innerShadow_1248_7583' result='effect2_innerShadow_1248_7583' />
            </filter>
          </defs>
        </svg>
      </div>
    </div>
  );
}
